import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "dropZone",
    "fileInput",
    "filePreview",
    "nameField",
    "categorySelect",
    "taxYearField",
    "fundSelect",
    "visibilityRadio",
    "visibleToAllField",
    "investorList",
    "orphanWarning",
    "selectedInvestorsContainer",
    "selectionCount"
  ]

  static values = {
    preselectedInvestorIds: { type: Array, default: [] }
  }

  connect() {
    // Track selected investor IDs across pagination
    this.selectedInvestorIds = new Set()
    // Track last clicked checkbox for shift-click range selection
    this.lastClickedCheckbox = null

    // Initialize with preselected investor IDs if provided
    if (this.preselectedInvestorIdsValue && this.preselectedInvestorIdsValue.length > 0) {
      this.preselectedInvestorIdsValue.forEach(id => {
        this.selectedInvestorIds.add(String(id))
      })
      // Update hidden fields and count immediately
      this.updateHiddenFields()
      this.updateSelectionCount()
    }

    this.setupDropZone()
    this.updateVisibilityState()
    this.updateOrphanWarning()
    this.setupTurboFrameListener()
  }

  setupTurboFrameListener() {
    // When the investor selection frame reloads, restore checkbox states and update UI
    document.addEventListener('turbo:frame-load', (event) => {
      if (event.target.id === 'investor-selection') {
        this.restoreCheckboxStates()
        this.updateOrphanWarning()
        this.updateSelectionCount()
        // Reset last clicked checkbox since the old reference is no longer valid
        this.lastClickedCheckbox = null
      }
    })
  }

  // Restore checkbox states after Turbo Frame reload
  restoreCheckboxStates() {
    this.getVisibleCheckboxes().forEach(checkbox => {
      const investorId = checkbox.value
      checkbox.checked = this.selectedInvestorIds.has(investorId)
    })
  }

  // Update the hidden fields that will be submitted with the form
  updateHiddenFields() {
    if (!this.hasSelectedInvestorsContainerTarget) return

    // Clear existing hidden fields
    this.selectedInvestorsContainerTarget.innerHTML = ''

    // Create a hidden field for each selected investor
    this.selectedInvestorIds.forEach(id => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = 'investor_account_ids[]'
      input.value = id
      this.selectedInvestorsContainerTarget.appendChild(input)
    })
  }

  // Update the visual selection counter
  updateSelectionCount() {
    if (!this.hasSelectionCountTarget) return

    const count = this.selectedInvestorIds.size
    if (count === 0) {
      this.selectionCountTarget.textContent = 'No investors selected'
      this.selectionCountTarget.classList.remove('text-primary-600', 'dark:text-primary-400')
      this.selectionCountTarget.classList.add('text-gray-500', 'dark:text-gray-400')
    } else {
      this.selectionCountTarget.textContent = `${count} investor${count === 1 ? '' : 's'} selected`
      this.selectionCountTarget.classList.remove('text-gray-500', 'dark:text-gray-400')
      this.selectionCountTarget.classList.add('text-primary-600', 'dark:text-primary-400', 'font-medium')
    }
  }

  setupDropZone() {
    if (!this.hasDropZoneTarget) return

    const dropZone = this.dropZoneTarget

    dropZone.addEventListener('dragover', (e) => {
      e.preventDefault()
      dropZone.classList.add('border-primary-500', 'bg-primary-50', 'dark:bg-primary-900/20')
    })

    dropZone.addEventListener('dragleave', (e) => {
      e.preventDefault()
      dropZone.classList.remove('border-primary-500', 'bg-primary-50', 'dark:bg-primary-900/20')
    })

    dropZone.addEventListener('drop', (e) => {
      e.preventDefault()
      dropZone.classList.remove('border-primary-500', 'bg-primary-50', 'dark:bg-primary-900/20')

      if (e.dataTransfer.files.length > 0) {
        this.fileInputTarget.files = e.dataTransfer.files
        this.showFilePreview(e.dataTransfer.files[0])
      }
    })
  }

  fileSelected(event) {
    const file = event.target.files[0]
    if (file) {
      this.showFilePreview(file)
    }
  }

  showFilePreview(file) {
    if (!this.hasFilePreviewTarget) return

    const preview = this.filePreviewTarget
    const fileSize = this.formatFileSize(file.size)
    const fileIcon = this.getFileIcon(file.type)

    preview.innerHTML = `
      <div class="flex items-center justify-center">
        ${fileIcon}
        <div class="ml-3 text-left">
          <p class="text-sm font-medium text-gray-900 dark:text-white">${this.escapeHtml(file.name)}</p>
          <p class="text-xs text-gray-500 dark:text-gray-400">${fileSize}</p>
        </div>
      </div>
    `

    // Auto-populate name field if empty
    if (this.hasNameFieldTarget && !this.nameFieldTarget.value) {
      // Remove file extension for the name
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "")
      this.nameFieldTarget.value = nameWithoutExt
    }
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  getFileIcon(mimeType) {
    const iconClass = 'w-8 h-8 text-gray-400'

    if (mimeType.includes('pdf')) {
      return `<svg class="${iconClass} text-red-500" fill="currentColor" viewBox="0 0 24 24">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4zM8.5 18a1 1 0 0 1-1-1v-1a1 1 0 0 1 1-1H10v-2H8.5a1 1 0 0 1-1-1v-1a1 1 0 0 1 1-1H12v7h-1v-6H9v1h1v4H9v1h-.5z"/>
      </svg>`
    } else if (mimeType.includes('word') || mimeType.includes('document')) {
      return `<svg class="${iconClass} text-blue-500" fill="currentColor" viewBox="0 0 24 24">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4z"/>
      </svg>`
    } else if (mimeType.includes('sheet') || mimeType.includes('excel')) {
      return `<svg class="${iconClass} text-green-500" fill="currentColor" viewBox="0 0 24 24">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4z"/>
      </svg>`
    } else if (mimeType.includes('image')) {
      return `<svg class="${iconClass} text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
      </svg>`
    }

    return `<svg class="${iconClass}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
    </svg>`
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  categoryChanged(event) {
    if (!this.hasTaxYearFieldTarget) return

    if (event.target.value === 'tax') {
      this.taxYearFieldTarget.classList.remove('hidden')
    } else {
      this.taxYearFieldTarget.classList.add('hidden')
    }
  }

  fundChanged(event) {
    // Clear selections when fund changes (investor pool is different)
    this.selectedInvestorIds.clear()
    this.updateHiddenFields()
    this.updateSelectionCount()

    const fundId = event.target.value

    // Reload the investor selection Turbo Frame with the selected fund
    const investorsUrl = event.target.dataset.investorsUrl
    if (investorsUrl) {
      const frame = document.querySelector('turbo-frame#investor-selection')
      if (frame) {
        const url = fundId ? `${investorsUrl}?fund_id=${fundId}` : investorsUrl
        frame.src = url
      }
    }

    // Reload the entity selection Turbo Frame with the selected fund
    const entitiesUrl = event.target.dataset.entitiesUrl
    if (entitiesUrl) {
      const entityFrame = document.querySelector('turbo-frame#entity-selection')
      if (entityFrame) {
        const url = fundId ? `${entitiesUrl}?fund_id=${fundId}` : entitiesUrl
        entityFrame.src = url
      }
    }

    this.updateOrphanWarning()
  }

  visibilityChanged(event) {
    this.updateVisibilityState()
    this.updateOrphanWarning()
  }

  updateVisibilityState() {
    const isFundVisible = this.visibilityRadioTargets.find(
      radio => radio.value === 'fund_visible' && radio.checked
    )

    if (this.hasInvestorListTarget) {
      if (isFundVisible) {
        this.investorListTarget.classList.add('hidden')
      } else {
        this.investorListTarget.classList.remove('hidden')
      }
    }

    if (this.hasVisibleToAllFieldTarget) {
      this.visibleToAllFieldTarget.value = isFundVisible ? 'true' : 'false'
    }
  }

  // Select ONLY the visible checkboxes on current page (clears other selections)
  selectAllOnPage() {
    // Clear all existing selections first
    this.selectedInvestorIds.clear()

    // Select only the investors on the current page
    this.getVisibleCheckboxes().forEach(checkbox => {
      checkbox.checked = true
      this.selectedInvestorIds.add(checkbox.value)
    })
    this.updateHiddenFields()
    this.updateSelectionCount()
    this.updateOrphanWarning()
  }

  // Select ALL investors across all pages (fetches from server)
  async selectAll(event) {
    const button = event.currentTarget
    const fundSelect = this.hasFundSelectTarget ? this.fundSelectTarget : null
    const fundId = fundSelect?.value || ''

    // Show loading state
    const originalText = button.textContent
    button.textContent = 'Loading...'
    button.disabled = true

    try {
      const url = new URL(button.dataset.selectAllUrl || '/documents/all_investor_ids', window.location.origin)
      if (fundId) url.searchParams.set('fund_id', fundId)

      const response = await fetch(url, {
        headers: { 'Accept': 'application/json' }
      })

      if (response.ok) {
        const data = await response.json()
        data.investor_ids.forEach(id => this.selectedInvestorIds.add(String(id)))
        this.restoreCheckboxStates()
        this.updateHiddenFields()
        this.updateSelectionCount()
        this.updateOrphanWarning()
      }
    } catch (error) {
      console.error('Failed to fetch all investor IDs:', error)
    } finally {
      button.textContent = originalText
      button.disabled = false
    }
  }

  // Clear all selections
  selectNone() {
    this.selectedInvestorIds.clear()
    this.getVisibleCheckboxes().forEach(cb => cb.checked = false)
    this.updateHiddenFields()
    this.updateSelectionCount()
    this.updateOrphanWarning()
  }

  // Handle individual checkbox click (with shift-click range selection)
  investorClicked(event) {
    const checkbox = event.target
    const investorId = checkbox.value
    const checkboxes = this.getVisibleCheckboxes()

    // Handle shift-click for range selection
    if (event.shiftKey && this.lastClickedCheckbox && this.lastClickedCheckbox !== checkbox) {
      const lastIndex = checkboxes.indexOf(this.lastClickedCheckbox)
      const currentIndex = checkboxes.indexOf(checkbox)

      if (lastIndex !== -1 && currentIndex !== -1) {
        const start = Math.min(lastIndex, currentIndex)
        const end = Math.max(lastIndex, currentIndex)

        // Select/deselect all checkboxes in the range (use the state of current checkbox)
        const shouldCheck = checkbox.checked
        for (let i = start; i <= end; i++) {
          const cb = checkboxes[i]
          cb.checked = shouldCheck
          if (shouldCheck) {
            this.selectedInvestorIds.add(cb.value)
          } else {
            this.selectedInvestorIds.delete(cb.value)
          }
        }
      }
    } else {
      // Normal click - just toggle this checkbox in our tracking
      if (checkbox.checked) {
        this.selectedInvestorIds.add(investorId)
      } else {
        this.selectedInvestorIds.delete(investorId)
      }
    }

    // Remember this checkbox for future shift-clicks
    this.lastClickedCheckbox = checkbox

    this.updateHiddenFields()
    this.updateSelectionCount()
    this.updateOrphanWarning()
  }

  getVisibleCheckboxes() {
    if (!this.hasInvestorListTarget) return []
    return Array.from(this.investorListTarget.querySelectorAll('.investor-checkbox'))
  }

  updateOrphanWarning() {
    if (!this.hasOrphanWarningTarget) return

    const isFundVisible = this.visibilityRadioTargets.find(
      radio => radio.value === 'fund_visible' && radio.checked
    )

    if (isFundVisible) {
      this.orphanWarningTarget.classList.add('hidden')
      return
    }

    // Use selectedInvestorIds.size to count selections across ALL pages
    if (this.selectedInvestorIds.size === 0) {
      this.orphanWarningTarget.classList.remove('hidden')
    } else {
      this.orphanWarningTarget.classList.add('hidden')
    }
  }
}
